import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { PulpPaperRoute } from '../../types';

/** 机械浆（磨石磨木浆）P&ID 详细图：剥皮 → 磨木 → 筛选漂白 + 白水回收循环 */
const media = {
  pulp: { label: '木段 / 纸浆', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  chem: { label: 'H₂O₂ / 漂剂', light: '#00838f', dark: '#4dd0e1', width: 2 },
  water: { label: '白水 / 回用水', light: '#1565c0', dark: '#64b5f6', width: 2.6 },
};

const devices = [
  { id: 'dc101', kind: 'hDrum' as const, x: 170, y: 189, tag: 'DC-101', name: '木段仓' },
  { id: 'dc102', kind: 'washer' as const, x: 340, y: 188, tag: 'DC-102', name: '剥皮鼓' },
  { id: 'm201', kind: 'refiner' as const, x: 510, y: 179, tag: 'M-201', name: '磨木机一', note: '磨石 ~60℃' },
  { id: 'm202', kind: 'refiner' as const, x: 660, y: 179, tag: 'M-202', name: '磨木机二' },
  { id: 'w301', kind: 'washer' as const, x: 810, y: 188, tag: 'W-301', name: '筛选净化' },
  { id: 'b301', kind: 'tower' as const, x: 960, y: 125, tag: 'B-301', name: '漂白塔', note: 'H₂O₂' },
  { id: 'w302', kind: 'washer' as const, x: 1110, y: 188, tag: 'W-302', name: '洗涤' },
  { id: 'c301', kind: 'clarifier' as const, x: 1440, y: 182, tag: 'C-301', name: '白水回收' },
  { id: 'j301', kind: 'mixer' as const, x: 495, y: 612, tag: '', name: '' },
];

const ports: PidPort[] = [
  { id: 'p-wood', label: '木段', medium: 'pulp', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-pulp', label: '机械浆', medium: 'pulp', x: 1290, y: 224, direction: 'out' as const },
  { id: 'p-chem', label: 'H₂O₂', medium: 'chem', x: 1005, y: 40, direction: 'in' as const },
  { id: 'p-white', label: '白水外排', medium: 'water', x: 1570, y: 470, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 'k01', from: 'p-wood', fromSide: 'right', to: 'dc101', toSide: 'left', medium: 'pulp' },
  { id: 'k02', from: 'dc101', fromSide: 'right', to: 'dc102', toSide: 'left', medium: 'pulp' },
  { id: 'k03', from: 'dc102', fromSide: 'right', to: 'm201', toSide: 'left', medium: 'pulp' },
  { id: 'k04', from: 'm201', fromSide: 'right', to: 'm202', toSide: 'left', medium: 'pulp' },
  { id: 'k05', from: 'm202', fromSide: 'right', to: 'w301', toSide: 'left', medium: 'pulp' },
  { id: 'k06', from: 'w301', fromSide: 'right', to: 'b301', toSide: 'left', medium: 'pulp' },
  { id: 'k07', from: 'b301', fromSide: 'right', to: 'w302', toSide: 'left', medium: 'pulp' },
  { id: 'k08', from: 'w302', fromSide: 'right', to: 'p-pulp', toSide: 'left', medium: 'pulp' },
  // 白水：洗涤机 → 白水回收 → 回用磨木机（底部车道）
  { id: 'k09', from: 'w302', fromSide: 'bottom', to: 'c301', toSide: 'left', medium: 'water' },
  { id: 'k10a', from: 'c301', fromSide: 'bottom', to: 'j301', toSide: 'top', medium: 'water' },
  { id: 'k10b', from: 'j301', fromSide: 'top', to: 'm201', toSide: 'bottom', medium: 'water', label: '回用白水' },
  { id: 'k11', from: 'c301', fromSide: 'right', to: 'p-white', toSide: 'left', medium: 'water' },
  { id: 'k12', from: 'p-chem', fromSide: 'right', to: 'b301', toSide: 'top', medium: 'chem' },
];

const MechanicalPulpDiagram: React.FC<{ route: PulpPaperRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="磨石磨木浆装置详细流程"
    subtitle="木段仓 → 剥皮鼓 → 两台磨木机（磨石机械磨浆，得率 ~95%）→ 筛选净化 → H₂O₂ 漂白 → 洗涤；白水经回收澄清器回用磨木机（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default MechanicalPulpDiagram;
